import React, { useState, useContext } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import Spinner from "../Spinner/Spinner";
import "firebase/firestore";
import {
  getAuth,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
} from "firebase/auth";
import { getFirestore, doc, setDoc } from "firebase/firestore";

import {
  Avatar,
  Box,
  Typography,
  Paper,
  Grid,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  TextField,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";

import CssBaseline from "@mui/material/CssBaseline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Container from "@mui/material/Container";
import { createTheme, ThemeProvider } from "@mui/material/styles";

import AmplitudeEvent from "../Amplitude/AmplitudeEvent";
import SnackbarComponent from "../Alerts/SnackbarComponent";
import { getErrorMessage } from "../Helper";
import { AuthContext } from "./Auth";

const theme = createTheme();

const SCHOOL_OPTIONS = [
  "Secacus High School",
  "Lincoln High School",
  "Roundpier"
];

function Copyright(props) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
    >
      {"Copyright © "}
      <Link to="/" color="inherit" href="https://roundpier.com">
        RoundPier
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

export default function SignUp() {
  AmplitudeEvent("/signup-loaded");

  const history = useNavigate();
  const [name, setName] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [role, setRole] = useState(""); // Optional: could be a dropdown
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [severity, setSeverity] = useState("success");

  const auth = getAuth();
  const provider = new GoogleAuthProvider();
  const db = getFirestore();

  const { currentUser } = useContext(AuthContext);

  if (currentUser) {
    return <Navigate to="/clubs" />;
  }

  const postData = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(auth.currentUser, { displayName: name });

      // Add user to Firestore
      await setDoc(doc(db, "users", auth.currentUser.uid), {
        uid: auth.currentUser.uid,
        name,
        email,
        role,
        schoolName,
        createdAt: new Date().toISOString(),
      });

      setSnackbarMsg("Successfully signed up!");
      setSeverity("success");
      setSnackbarOpen(true);
      history("/clubs");
    } catch (e) {
      const message = getErrorMessage(e);
      setSnackbarMsg(message);
      setSeverity("error");
      setSnackbarOpen(true);
      setLoading(false);
    }
  };

  const googleAuth = async () => {
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Add user to Firestore
      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        name: user.displayName || "",
        email: user.email,
        role: "", // You can collect role later if needed
        createdAt: new Date().toISOString(),
      });

      setSnackbarMsg("Successfully signed up with Google!");
      setSeverity("success");
      setSnackbarOpen(true);
      history("/clubs");
    } catch (e) {
      const message = getErrorMessage(e);
      setSnackbarMsg(message);
      setSeverity("error");
      setSnackbarOpen(true);
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMsg("");
    setSeverity("success");
  };

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="xs">
        {snackbarOpen && (
          <SnackbarComponent
            snackbarOpen={snackbarOpen}
            snackbarMsg={snackbarMsg}
            severity={severity}
            handleSnackbarClose={handleSnackbarClose}
          />
        )}
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Sign Up
          </Typography>
          <Box component="form" onSubmit={postData} noValidate sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label="Name"
              name="name"
              autoComplete="name"
              autoFocus
              onChange={(event) => setName(event.target.value)}
            />

            <FormControl fullWidth>
              <InputLabel>Select School</InputLabel>
              <Select
                value={schoolName}
                label="Select School"
                onChange={(e) => setSchoolName(e.target.value)}
              >
                {/* No school option */}
                <MenuItem value="">
                  <em>No school – please contact info@roundpier.com</em>
                </MenuItem>

                {/* Existing school options */}
                {SCHOOL_OPTIONS.map((school) => (
                  <MenuItem key={school} value={school}>
                    {school}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl component="fieldset" margin="normal" fullWidth>
              <FormLabel component="legend">Role</FormLabel>
              <RadioGroup
                row
                aria-label="role"
                name="role"
                value={role}
                onChange={(event) => setRole(event.target.value)}
              >
                <FormControlLabel value="Teacher" control={<Radio />} label="Teacher" />
                <FormControlLabel value="Student" control={<Radio />} label="Student" />
              </RadioGroup>
            </FormControl>

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              onChange={(event) => setEmail(event.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              onChange={(event) => setPassword(event.target.value)}
            />
            {/* <Grid item>
              <Link to="/terms" href="#" variant="body2">
                {"Agree to RoundPier Terms and Conditions"}
              </Link>
            </Grid> */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? "Signing Up..." : "Agree & Sign Up"}
            </Button>
            <Grid container>
              {/* <Button
                type="button"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, backgroundColor: "blueviolet" }}
                onClick={googleAuth}
                disabled={loading}
              >
                {loading ? "Signing Up..." : "Agree & Sign up with Google"}
              </Button> */}
              <Grid item>
                <Link to="/signin" href="#" variant="body2">
                  {"Already have an account? Sign In"}
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Copyright sx={{ mt: 8, mb: 4 }} />
      </Container>
    </ThemeProvider>
  );
}
